import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef,
} from 'react';
import { GENERIC_ERROR } from './constants';
import { uid } from './utils';

const NovaContext = createContext(null);

const EMPTY_DRAFTS = { chat: '', insights: '' };

const initialState = {
    open: false,
    activeTab: 'chat',
    drafts: EMPTY_DRAFTS,
    unread: false,
    messages: [],
    sending: false,
    error: null,
    credits: null,
    insights: { status: 'idle', data: null },
};

function reducer(state, action) {
    switch (action.type) {
        case 'open':
            return { ...state, open: true, unread: false };
        case 'minimize':
            return { ...state, open: false };
        case 'close':
            return { ...state, open: false, drafts: EMPTY_DRAFTS };
        case 'tab':
            return { ...state, activeTab: action.tab };
        case 'draft':
            return { ...state, drafts: { ...state.drafts, [action.tab]: action.value } };
        case 'chat/reset':
            return { ...state, messages: [], sending: false, error: null };
        case 'chat/send':
            return { ...state, messages: [...state.messages, action.message], sending: true, error: null, activeTab: 'chat' };
        case 'chat/resend':
            return { ...state, sending: true, error: null };
        case 'chat/truncate':
            return { ...state, messages: action.messages, sending: true, error: null };
        case 'chat/reply':
            return {
                ...state,
                messages: [...state.messages, action.message],
                credits: action.credits ?? state.credits,
                sending: false,
                unread: state.unread || !state.open,
            };
        case 'chat/fail':
            return { ...state, sending: false, error: action.error };
        case 'insights/load':
            return { ...state, insights: { status: 'loading', data: state.insights.data } };
        case 'insights/ready':
            return { ...state, insights: { ...state.insights, status: 'ready', data: action.data } };
        case 'insights/error':
            return { ...state, insights: { ...state.insights, status: 'error' } };
        default:
            return state;
    }
}

export function NovaProvider({ api, status = 'online', children }) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const stateRef = useRef(state);
    stateRef.current = state;

    const launcherRef = useRef(null);
    const inputRef = useRef(null);
    const abortRef = useRef(null);
    const insightsRequest = useRef(0);
    const wasOpen = useRef(false);
    const restoreFocus = useRef(true);

    const open = useCallback(() => dispatch({ type: 'open' }), []);
    const minimize = useCallback(() => dispatch({ type: 'minimize' }), []);
    const dismiss = useCallback(() => {
        restoreFocus.current = false;
        dispatch({ type: 'minimize' });
    }, []);
    const close = useCallback(() => dispatch({ type: 'close' }), []);
    const toggle = useCallback(() => dispatch({ type: stateRef.current.open ? 'minimize' : 'open' }), []);
    const setTab = useCallback((tab) => dispatch({ type: 'tab', tab }), []);
    const setDraft = useCallback((tab, value) => dispatch({ type: 'draft', tab, value }), []);
    const focusInput = useCallback(() => inputRef.current?.focus(), []);

    const request = useCallback(async (input) => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            const { message, remainingCredits } = await api.sendMessage(input, controller.signal);
            if (controller.signal.aborted) return;
            dispatch({ type: 'chat/reply', message, credits: remainingCredits });
        } catch (error) {
            if (controller.signal.aborted) return;
            dispatch({ type: 'chat/fail', error: { text: input, message: error?.userMessage || GENERIC_ERROR } });
        }
    }, [api]);

    const send = useCallback((raw) => {
        const input = raw.trim();
        const { sending, credits } = stateRef.current;
        if (!input || sending || credits === 0) return;
        dispatch({
            type: 'chat/send',
            message: { id: uid(), role: 'user', text: input, createdAt: new Date().toISOString() },
        });
        request(input);
    }, [request]);

    const retry = useCallback(() => {
        const failed = stateRef.current.error;
        if (!failed?.text) return;
        dispatch({ type: 'chat/resend' });
        request(failed.text);
    }, [request]);

    const regenerate = useCallback((messageId) => {
        const { messages, sending, credits } = stateRef.current;
        if (sending || credits === 0) return;
        const index = messages.findIndex((m) => m.id === messageId);
        const prompt = messages.slice(0, Math.max(index, 0)).reverse().find((m) => m.role === 'user');
        if (index < 0 || !prompt) return;
        dispatch({ type: 'chat/truncate', messages: messages.slice(0, index) });
        request(prompt.text);
    }, [request]);

    const newChat = useCallback(() => {
        abortRef.current?.abort();
        dispatch({ type: 'chat/reset' });
        inputRef.current?.focus();
    }, []);

    const loadInsights = useCallback(async () => {
        insightsRequest.current += 1;
        const id = insightsRequest.current;
        dispatch({ type: 'insights/load' });
        try {
            const data = await api.getInsights();
            if (id === insightsRequest.current) dispatch({ type: 'insights/ready', data });
        } catch {
            if (id === insightsRequest.current) dispatch({ type: 'insights/error' });
        }
    }, [api]);

    useEffect(() => {
        if (state.open && state.insights.status === 'idle') loadInsights();
    }, [state.open, state.insights.status, loadInsights]);

    useEffect(() => {
        if (state.open) inputRef.current?.focus();
    }, [state.open]);

    useEffect(() => {
        if (wasOpen.current && !state.open) {
            if (restoreFocus.current) launcherRef.current?.focus();
            restoreFocus.current = true;
        }
        wasOpen.current = state.open;
    }, [state.open]);

    useEffect(() => () => abortRef.current?.abort(), []);

    const actions = useMemo(() => ({
        open,
        minimize,
        dismiss,
        close,
        toggle,
        setTab,
        setDraft,
        focusInput,
        send,
        retry,
        regenerate,
        newChat,
        reloadInsights: loadInsights,
    }), [open, minimize, dismiss, close, toggle, setTab, setDraft, focusInput, send, retry, regenerate, newChat, loadInsights]);

    const value = useMemo(
        () => ({ state, status, actions, refs: { launcherRef, inputRef } }),
        [state, status, actions],
    );

    return <NovaContext.Provider value={value}>{children}</NovaContext.Provider>;
}

export function useNova() {
    const value = useContext(NovaContext);
    console.log("opop", value);
    
    if (!value) throw new Error('useNova must be used inside NovaProvider');
    return value;
}
